/**
 * Ban Notification Domain
 * The removal notice a banned player sees, and the handler that opens it: as
 * the notice is routed, the ban ends the session's reconnecting and cuts
 * voice. A ban lasts a minute (BanDurationSeconds), so the player is told it
 * as a removal, and when they can come back; the moderator's id and the
 * portal's fixed English reason the server passes on are not shown.
 */

import { create } from 'zustand';
import type { MessageType } from '../constants/op-codes';
import { nakamaService } from '../services/nakama/nakama-service';
import { endPlay } from './end-play';
import { handleVoiceBan } from './voice-chat';

// ==================== TYPES ====================

interface BanData {
  /** When the player can come back, in Unix seconds. */
  expiresAt: number;
}

/** The ban notice as the server sends it (banned-players.go): only when it ends is read. */
export interface PlayerBannedMessage {
  type: typeof MessageType.MessageTypePlayerBanned;
  /** The portal's words, in English: not shown. */
  reason?: string;
  /** Unix seconds. */
  expires_at: number;
  /** The moderator's sign-in id: not shown. */
  banned_by?: string;
}

// ==================== STORE ====================

interface BanNotificationState {
  /** The removal the notice says, while it is open. */
  banData: BanData | null;
  setBanData: (data: BanData) => void;
  reset: () => void;
}

export const useBanNotificationStore = create<BanNotificationState>((set) => ({
  banData: null,
  setBanData: (data: BanData) => set({ banData: data }),
  reset: () => set({ banData: null }),
}));

// ==================== HANDLERS ====================

/**
 * The server has banned this player from the match, and closes their socket
 * next (banned-players.go). All of it happens as the notice is routed, before
 * that close can be handled: the ban ends the session's reconnecting for good
 * (nakamaService.stopReconnecting), so the close starts no reconnect, and one
 * the close already started — a long notice is decompressed first — stops at
 * once; play ends (endPlay) — a moderator's call or invitation with it, its
 * mute given back, and the command on its way; voice is cut, so its tokens
 * cannot rejoin a room; and the ban notice opens, the one thing on screen
 * until the player acts on it.
 */
export function handlePlayerBanned(data: PlayerBannedMessage): void {
  nakamaService.stopReconnecting();
  endPlay();
  handleVoiceBan();
  useBanNotificationStore.getState().setBanData({ expiresAt: data.expires_at });
}
